import { useEffect, useMemo, useState } from "react";
import { addCartItem, getCart } from "../service/CartService";
import { getCategories, getFoods } from "../service/CatalogService";
import { Alert } from "../util/Alert";
import { Loading } from "../util/Loading";
import { Cart, Category, FoodItem } from "../model/types";
import { extractErrorMessage } from "../service/api";

export const FoodCatalog = () => {
  const [foods, setFoods] = useState<FoodItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | "ALL">(
    "ALL"
  );
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  const loadData = async () => {
    setLoading(true);
    try {
      const [foodsData, categoriesData] = await Promise.all([
        getFoods(true),
        getCategories(),
      ]);
      setFoods(foodsData);
      setCategories(categoriesData);
      try {
        setCart(await getCart());
      } catch {
        setCart(null);
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredFoods = useMemo(() => {
    return foods.filter((food) => {
      const matchesCategory =
        selectedCategory === "ALL" || food.categoryId === selectedCategory;
      const matchesSearch = `${food.name} ${food.description}`
        .toLowerCase()
        .includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [foods, search, selectedCategory]);

  const totalAvailableUnits = filteredFoods.reduce(
    (sum, food) =>
      sum +
      Math.max(
        0,
        food.stockQuantity -
          (cart?.items.find((item) => item.foodItemId === food.id)?.quantity ?? 0)
      ),
    0
  );

  const getReservedQuantity = (foodId: number) =>
    cart?.items.find((item) => item.foodItemId === foodId)?.quantity ?? 0;

  const handleAddToCart = async (foodId: number) => {
    setError("");
    setMessage("");
    try {
      const nextCart = await addCartItem({
        foodItemId: foodId,
        quantity: quantities[foodId] || 1,
      });
      setCart(nextCart);
      setMessage("Item added to cart");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <Loading label="Loading menu..." />;
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-4 rounded-3xl bg-white p-6 shadow-sm lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-emerald-700">
            Menu
          </p>
          <h2 className="mt-2 text-3xl font-semibold text-slate-900">
            Browse available food items
          </h2>
          <p className="mt-3 text-sm text-stone-600">
            {filteredFoods.length} menu items available, {totalAvailableUnits} total units ready to order.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            type="search"
            placeholder="Search food"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="rounded-2xl border border-stone-300 px-4 py-3"
          />
          <select
            value={selectedCategory}
            onChange={(event) =>
              setSelectedCategory(
                event.target.value === "ALL" ? "ALL" : Number(event.target.value)
              )
            }
            className="rounded-2xl border border-stone-300 px-4 py-3"
          >
            <option value="ALL">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {message ? (
        <Alert type="success" message={message} onClose={() => setMessage("")} />
      ) : null}
      {error ? (
        <Alert type="failed" message={error} onClose={() => setError("")} />
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filteredFoods.map((food) => (
          <article key={food.id} className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
            {(() => {
              const reservedQuantity = getReservedQuantity(food.id);
              const availableToAdd = Math.max(0, food.stockQuantity - reservedQuantity);
              return (
                <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-stone-500">
                  {food.categoryName}
                </p>
                <h3 className="mt-2 text-xl font-semibold text-slate-900">
                  {food.name}
                </h3>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                  {availableToAdd} available to add
                </p>
                {reservedQuantity > 0 ? (
                  <p className="mt-1 text-xs text-stone-500">
                    {reservedQuantity} already in your cart
                  </p>
                ) : null}
              </div>
              <div className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                Rs. {food.price.toFixed(2)}
              </div>
            </div>
            <p className="mt-4 text-sm leading-6 text-stone-600">
              {food.description}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={Math.max(1, availableToAdd)}
                value={quantities[food.id] || 1}
                onChange={(event) =>
                  setQuantities((prev) => ({
                    ...prev,
                    [food.id]: Math.min(
                      Math.max(1, availableToAdd),
                      Math.max(1, Number(event.target.value))
                    ),
                  }))
                }
                className="w-20 rounded-2xl border border-stone-300 px-3 py-2"
              />
              <button
                type="button"
                onClick={() => handleAddToCart(food.id)}
                disabled={availableToAdd <= 0}
                className="flex-1 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
              >
                {availableToAdd <= 0 ? "Max in cart" : "Add to cart"}
              </button>
            </div>
                </>
              );
            })()}
          </article>
        ))}
      </div>
    </section>
  );
};
